/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react';

import loadable, { loadableReady } from '@loadable/component';
import PropTypes from 'prop-types';
import { registerServiceWorker } from 'pwa-kit-react-sdk/ssr/browser/main';
import { routeComponent } from 'pwa-kit-react-sdk/ssr/universal/components/route-component';
import DeviceContext from 'pwa-kit-react-sdk/ssr/universal/device-context';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router, Switch as RouterSwitch, Route } from 'react-router-dom';
import { UIDReset, UIDFork } from 'react-uid';

import AppConfig from './components/_app-config/index';
import App from './components/_app/index';
import ShoeFinderResults from './views/shoe-finder-results/ShoeFinderResults';

const fallback = <div>fallback</div>;
const BraFinderWizard = loadable(() => import('./views/bra-finder-wizard/BraFinderWizard'), { fallback });
// const ShoeFinderResults = loadable(() => import('./views/shoe-finder-results/ShoeFinderResults'), { fallback });
const ShoeFinderWizard = loadable(() => import('./views/shoe-finder-wizard/ShoeFinderWizard'), { fallback });

const Switch = (props) => {
  const { App: PropsApp, appState, error, routes, sfccData } = props;
  return (
    <UIDReset>
      {!error && (
        <PropsApp preloadedProps={appState.appProps}>
            <RouterSwitch>
              {routes.map((route, i) => {
                const { component: Component, ...routeProps } = route;
                return (
                  <Route
                    key={i} // eslint-disable-line
                    {...routeProps}
                  >
                    <UIDFork>
                        <Component preloadedProps={appState.pageProps} sfccData={sfccData} />
                    </UIDFork>
                  </Route>
                );
              })}
            </RouterSwitch>
        </PropsApp>
      )}
    </UIDReset>
  );
};

Switch.propTypes = {
  App: PropTypes.func, // eslint-disable-line
  appState: PropTypes.object, // eslint-disable-line
  error: PropTypes.object, // eslint-disable-line
  preloadedProps: PropTypes.object, // eslint-disable-line
  routes: PropTypes.array, // eslint-disable-line
  sfccData: PropTypes.object, // eslint-disable-line
};

export const start2 = async () => {
  const rootEl = document.getElementsByClassName('react-target')[0];
  const data = JSON.parse(document.getElementById('mobify-data').innerHTML);

  let rootDataElement = document.getElementById('shoefinder-data');
  if (!rootDataElement) {
    rootDataElement = document.createElement('div');
    rootDataElement.setAttribute('id', 'shoefinder-data');
    document.body.appendChild(rootDataElement);
  }

  const siteMap = {
    BraFinder: BraFinderWizard,
    Results: ShoeFinderResults,
    Wizard: ShoeFinderWizard,
  };

  const urls = {
    localhost: 'http://localhost:3000',
    dev: 'https://aa-dev.mobify-storefront.com',
    staging: 'https://aa-staging.mobify-storefront.com',
    production: 'https://aa-production.mobify-storefront.com',
    test: 'https://aa-test.mobify-storefront.com',
  };
  
  const { hostname, pathname, search } = window.location;

  const sanitizedQuotesPath = (path) => {
    const updatedPath = path;
    if(updatedPath.includes('//')) {
      const regex = /\/\//gi;
      return updatedPath.replace(regex, '/');
    }
      return updatedPath;
  };

  const getUpdatedPath = (path) => {
    const pathWhiteList = new Set();
    pathWhiteList.add('shoefinder');
    pathWhiteList.add('shoe-finder');
    pathWhiteList.add('shoefinder-results');
    let updatedPath = path;
    pathWhiteList.forEach((ele) => {
      if(path.includes(ele)) {
        updatedPath = path.replace(ele, '');
      }
    });

    return sanitizedQuotesPath(updatedPath);
  };
  
  let baseUrl = '';
  Object.entries(urls).forEach(([key, value]) => {
    if(hostname?.includes(key)) {
      baseUrl = value;
    }
  });

  const getPriorityWizardId = (params) => {
    const urlSearchParams = new URLSearchParams(params);
    const wizard = urlSearchParams.get('wiz');
    return wizard;
  };

  // Get Wizard ID for local and mobify environments.
  const getWizardId = (path) => {
    // Static configuration for local development and mobify link
    const contentfulWizardIds = {
      '/us/': '5iTuAlPP0KgtIewhnCccto',
      '/jp/': '3qHksqa3Mte4FaYhKdOLfG',
      '/au/': 'J79SrdK7KYFaKtSZdeoOe',
    };

    // Read wizard id from search and override static wizard ids
    const priorityWizardId = getPriorityWizardId(search);
    if(priorityWizardId) {
      return priorityWizardId;
    }

    // Read static wizard id if priorityWizardId not provided in search.
    let wizardId = '';
    if(path.includes('/us/')) {
      wizardId = contentfulWizardIds['/us/'];
    } else if(path.includes('/jp/')) {
      wizardId = contentfulWizardIds['/jp/'];
    } else if(path.includes('/au/')) {
      wizardId = contentfulWizardIds['/au/'];
    } else {
      wizardId = '5iTuAlPP0KgtIewhnCccto';
    }

    return wizardId;
  };

  // Get results url for local and mobify environments.
  const getResultsUrl = (url, path) => {
    const baseRoute = 'shoefinder-results';
    const updatedPath = `${url}${path}${baseRoute}`;
    return updatedPath;
  };

  // Get wizard url for local and mobify environments.
  const getWizardUrl = (url, path) => {
    const baseRoute = 'shoe-finder';
    const updatedPath = `${url}${path}${baseRoute}`;
    return updatedPath;
  };

  // Get shoefinder default DOM data for local and mobify environments.
  const getShoefinderDefaultData = (base, path = '/us/en-US/') => {
    const updatedPath = getUpdatedPath(path);
    return {
      component: 'Wizard',
      contentfulWizardId: getWizardId(updatedPath),
      resultsUrl: getResultsUrl(base, updatedPath),
      wizardUrl: getWizardUrl(base, updatedPath),
    };
  };
 
  baseUrl = baseUrl.length > 0 ? baseUrl : 'https://aa-production.mobify-storefront.com';
  // Used for local dev
  if (!rootDataElement.dataset.shoefinderdata) {
    rootDataElement.setAttribute('data-shoefinderdata', JSON.stringify({
      ...getShoefinderDefaultData(baseUrl, pathname),
    }));
  }

  const sfccData = JSON.parse(rootDataElement.dataset.shoefinderdata);

  // Set all globals sent from the server on the window object.
  Object.entries(data).forEach(([key, value]) => {
  window[key] = value;
  });

  // Tell webpack how to find javascript files
  Object.defineProperty(__webpack_require__, 'p', { // eslint-disable-line
      get: () => window.Progressive.buildOrigin,
  });

  // On the browser we don't have request.locals, so we just provide an empty
  // object that exists for the lifetime of the app. AppConfig components can use
  // this to set up, eg. Redux stores.
  const locals = {};

  // AppConfig.restore *must* come before getRoutes()
  AppConfig.restore(locals, window.__PRELOADED_STATE__.__STATE_MANAGEMENT_LIBRARY);

  // Make routes * based on what the dom says
  // create and one set of routes for local development
  const routes = [
    {
      path: '/:locale/brafinder',
      component: BraFinderWizard,
    },
    {
      path: '/:country/:locale/shoe-finder',
      component: ShoeFinderWizard,
    },
    {
      path: '/:country/:locale/shoefinder-results',
      component: ShoeFinderResults,
    },
    {
      path: '*',
      component: siteMap[sfccData.component] || ShoeFinderWizard,
    },
  ];

  // We need to tell the routeComponent HOC when the app is hydrating in order to
  // prevent pages from re-fetching data on the first client-side render. The
  // reason we do this is that we expect a render to have taken place
  // on the server already. That server-side render already called getProps()
  // and froze the application state as a JSON blob on the page.
  //
  // This is VERY fiddly – don't go crazy with window.__HYDRATING__. You have
  // been warned.
  window.__HYDRATING__ = true;

  const WrappedApp = routeComponent(App, false, locals);
  const error = window.__ERROR__;

  return Promise.resolve()
    .then(() => new Promise((resolve) => loadableReady(resolve)))
    .then(() => {
      ReactDOM.hydrate(
        <Router>
          <DeviceContext.Provider value={{ type: window.__DEVICE_TYPE__ }}>
            <AppConfig locals={locals}>
              <Switch
                App={WrappedApp}
                appState={window.__PRELOADED_STATE__}
                error={error}
                routes={routes}
                sfccData={sfccData}
              />
            </AppConfig>
          </DeviceContext.Provider>
        </Router>,
        rootEl,
        () => { window.__HYDRATING__ = false; },
      );
    });
};

const main2 = () => {
  return Promise.all([start2(), registerServiceWorker('/worker.js')]);
};

main2();

// ORIGINAL STARTUP CODE. THE REST IS HACKY
// const main = () => {
//   return Promise.all([start(), registerServiceWorker('/worker.js')]);
// };

// main();
